import { useQuery, useQueryClient } from '@tanstack/react-query';

import { backendApis } from '@/backendApis';
import { ModalsManager } from '@/components/modals/modalsManager';
import {
  availableIntegrationsQueryOptions,
  useConnectedAccountsQuery,
} from '@/features/integrations/integrations.query';
import type { CachedConnectedAccount } from '@/features/integrations/integrations.types';
import { msgChannelKeys } from '@/features/msg-channels/common/msg-channels.query';
import type { MessagingChannelType } from '@/features/msg-channels/common/msg-channels.types';
import { IMessageChannelConnectModal } from '@/features/msg-channels/imessage/IMessageChannelConnectModal';
import { WhatsAppChannelConnectModal } from '@/features/msg-channels/whatsapp/WhatsAppChannelConnectModal';
import { connectOAuthIntegration } from '@/integrations/integration-handlers';
import { openMcpReconnectModal } from '@/integrations/integrations.utils';

import {
  integrationManagementKeys,
  invalidateProviderAfterAccountChange,
  isChannelSetting,
} from './integration-management.query';

import type {
  IntegrationAccount,
  IntegrationProvider,
} from '@littlebird/integrations';

export interface ProviderAccountActions {
  canAdd: boolean;
  add(): Promise<void>;
  reconnect(account: IntegrationAccount): Promise<void>;
  /** Re-probes an `unreachable` account instead of launching OAuth. */
  recheck(): Promise<void>;
  rename(account: IntegrationAccount, label: string): Promise<void>;
  setEnabled(account: IntegrationAccount, enabled: boolean): Promise<void>;
  remove(account: IntegrationAccount): Promise<void>;
}

function channelTypeOf(provider: IntegrationProvider): MessagingChannelType {
  const setting = provider.settings.find(isChannelSetting);
  if (!setting) {
    throw new Error(
      `Channel provider "${provider.key}" has no channel setting`
    );
  }
  return setting.channel;
}

function channelConnectModalKey(channel: MessagingChannelType) {
  return channel === 'imessage'
    ? IMessageChannelConnectModal
    : WhatsAppChannelConnectModal;
}

function findRawComposioAccount(
  accounts: readonly CachedConnectedAccount[] | undefined,
  accountId: string | undefined
): CachedConnectedAccount | undefined {
  return accountId
    ? accounts?.find((account) => account.id === accountId)
    : undefined;
}

// OAuth-initiating calls (reconnect/add another account) need the real toolkit
// slug, which IntegrationAccount never carries — it's looked up by id in the
// already-cached raw Composio accounts, never inferred from a display name.
function requireToolkitSlug(
  accounts: readonly CachedConnectedAccount[] | undefined,
  accountId: string | undefined
): string {
  const raw = findRawComposioAccount(accounts, accountId);
  if (!raw) {
    throw new Error(`No cached composio account for id "${accountId}"`);
  }
  return raw.toolkit_name;
}

export function useProviderAccountActions(
  provider: IntegrationProvider
): ProviderAccountActions {
  const queryClient = useQueryClient();
  const isComposio = provider.kind === 'composio';
  const isMcp =
    provider.kind === 'catalog_mcp' || provider.kind === 'custom_mcp';

  const connectedAccountsQuery = useConnectedAccountsQuery(
    undefined,
    isComposio
  );
  const availableIntegrationsQuery = useQuery({
    ...availableIntegrationsQueryOptions(),
    enabled: isComposio,
  });

  const invalidateAfterMutation = () =>
    Promise.all([
      invalidateProviderAfterAccountChange(queryClient, provider),
      queryClient.invalidateQueries({
        queryKey: integrationManagementKeys.tools(provider.key),
      }),
    ]).then(() => undefined);

  const primaryToolkitSlug = isComposio
    ? findRawComposioAccount(
        connectedAccountsQuery.data,
        provider.accounts[0]?.id
      )?.toolkit_name
    : undefined;

  const canAdd = isComposio
    ? Boolean(
        primaryToolkitSlug &&
        availableIntegrationsQuery.data?.find(
          (info) => info.toolkit_name === primaryToolkitSlug
        )?.multi_account_enabled
      )
    : isMcp;

  async function add(): Promise<void> {
    if (isComposio) {
      const toolkitSlug = requireToolkitSlug(
        connectedAccountsQuery.data,
        provider.accounts[0]?.id
      );
      const result = await connectOAuthIntegration(toolkitSlug, provider.name);
      if (!result.success) {
        throw new Error(result.error ?? `Failed to connect ${provider.name}`);
      }
      return;
    }
    if (isMcp) {
      const serverId = provider.accounts[0]?.id;
      if (!serverId) {
        throw new Error(`Provider "${provider.key}" has no account to add to`);
      }
      const result = await backendApis.mcp.addAccount(serverId, {});
      openMcpReconnectModal(result, provider.name, { forceInBrowser: true });
      await invalidateAfterMutation();
      return;
    }
    throw new Error(
      `Cannot add an account for provider kind "${provider.kind}"`
    );
  }

  async function reconnect(account: IntegrationAccount): Promise<void> {
    if (provider.kind === 'channel') {
      const channel = channelTypeOf(provider);
      const connection = queryClient.getQueryData<{
        identifier: string;
      } | null>(msgChannelKeys.connection(channel));
      ModalsManager.show(channelConnectModalKey(channel), {
        currentIdentifier: connection?.identifier ?? null,
      });
      return;
    }
    if (account.source === 'composio') {
      const toolkitSlug = requireToolkitSlug(
        connectedAccountsQuery.data,
        account.id
      );
      const result = await connectOAuthIntegration(
        toolkitSlug,
        provider.name,
        account.label || undefined,
        undefined,
        undefined,
        account.id
      );
      if (!result.success) {
        throw new Error(result.error ?? `Failed to reconnect ${provider.name}`);
      }
      return;
    }
    const result = await backendApis.mcp.reconnectServer(account.id);
    // Server name, never the account label: the modal reuses this as the
    // server name if the user falls back to an API key, and a label would
    // create a second server named after it instead of fixing this row.
    openMcpReconnectModal(result, provider.name);
    await invalidateAfterMutation();
  }

  // Invalidating the provider's account query re-triggers useMCPServersQuery's
  // queryFn, which re-probes the servers list in the background (see
  // mcp.query.ts) — no dedicated single-account probe endpoint is wired.
  async function recheck(): Promise<void> {
    await invalidateProviderAfterAccountChange(queryClient, provider);
  }

  async function rename(
    account: IntegrationAccount,
    label: string
  ): Promise<void> {
    if (account.source === 'composio') {
      await backendApis.integrations.updateAccountLabel(account.id, label);
    } else {
      await backendApis.mcp.updateServerLabel(account.id, label);
    }
    await invalidateAfterMutation();
  }

  async function setEnabled(
    account: IntegrationAccount,
    enabled: boolean
  ): Promise<void> {
    if (account.source === 'composio') {
      await backendApis.integrations.toggleIntegrationById(account.id, enabled);
    } else {
      await backendApis.mcp.setServerEnabled(account.id, enabled);
    }
    await invalidateAfterMutation();
  }

  async function remove(account: IntegrationAccount): Promise<void> {
    if (provider.kind === 'channel') {
      await backendApis.messagingChannels.deleteConnection(
        channelTypeOf(provider)
      );
    } else if (account.source === 'composio') {
      await backendApis.integrations.disconnectIntegration(account.id);
    } else {
      await backendApis.mcp.deleteServer(account.id);
    }
    await invalidateAfterMutation();
  }

  return { canAdd, add, reconnect, recheck, rename, setEnabled, remove };
}
